import type {AppDefinitionId, CurrentRouteInfo} from '@wix/document-services-types'
import type {DistributePayload} from '@wix/ambassador-document-management-document-store-v1-transaction/types'
import type {LivePreviewRefreshOptions} from './sharedStateTypes'
import type {PublishPipelineStatus} from '../publishPipeline/publishPipeline'

export interface Message extends DistributePayload {
    /** message id */
    id: string
    /** message type */
    messageType: string
    /** data distribute - limited to 1 KB */
    data: Record<string, any> | null
}

export interface Test extends Message {
    messageType: 'test'
    data: {}
}

export interface PlatformSharedAPIState extends Message {
    messageType: 'platformSharedAPIState'
    data: {appDefinitionId: AppDefinitionId}
}

export interface OpenPlatformPanel extends Message {
    messageType: 'openPlatformPanelsState'
    data: {appDefinitionId: AppDefinitionId; action: string}
}

export interface PlatformSharedManifestState extends Message {
    messageType: 'platformSharedManifestState'
    data: {appDefinitionId: AppDefinitionId}
}

export interface TpaCompPreviewDataChange extends Message {
    messageType: 'tpaCompPreviewDataChange'
    data: {compId: string; data: any}
}

export interface TpaCompStateChange extends Message {
    messageType: 'tpaCompStateChange'
    data: {compId: string; data: any}
}

export interface TpaPostMessageToApp extends Message {
    messageType: 'tpaPostMessageToApp'
    data: {compId: string; eventType: string; params: any}
}

export interface LivePreviewSharedStateMessage extends Message {
    messageType: 'livePreviewSharedState'
    data: {livePreviewRefreshOptions: LivePreviewRefreshOptions}
}

export interface LivePreviewRoutersSharedStateMessage extends Message {
    messageType: 'routersConcurrentInvalidationState'
    data: {currentRouteInfo: CurrentRouteInfo; selectedRoute: string}
}

export interface WixCodeFileStateChange extends Message {
    messageType: 'wixCodeFileStateChange'
    data: {changedPaths: string[]}
}

export interface PublishPipelineMessage extends Message {
    messageType: PublishPipelineStatus
    data: {deploymentId: string; pipelines: string[]}
}

export interface ViewerCacheReadyMessage extends Message {
    messageType: 'VIEWER_CACHE_READY'
    data: {deploymentId: string}
}

export interface TriggerCreateRevision extends Message {
    messageType: 'CONCURRENT_EDITING_SHOULD_TRIGGER_CREATE_REVISION'
}

export interface ManifestUpdateMessage extends Message {
    messageType: 'MANIFEST_UPDATE'
}

export interface ComponentOverrideIdUpdate extends Message {
    messageType: 'componentOverrideIdUpdate'
    data: {appDefinitionId: AppDefinitionId; overrideId: string}
}

export interface CustomComponentServerCloneDone extends Message {
    messageType: 'customComponentServerCloneDone'
    data: {appDefinitionId: AppDefinitionId; overrideId: string}
}

export interface CustomComponentServerRestoreDone extends Message {
    messageType: 'customComponentServerRestoreDone'
    data: {appDefinitionId: AppDefinitionId; overrideId: string}
}

export interface TransactionsDroppedMessage extends Message {
    messageType: 'transactionsDropped'
    data: {}
}

export interface SitePropertiesInfoMessage extends Message {
    messageType: 'sitePropertiesInfoChanged'
    data: {siteName?: string; publicUrl?: string}
}

export type DistributeMessage =
    | Test
    | PlatformSharedAPIState
    | OpenPlatformPanel
    | PlatformSharedManifestState
    | TpaCompPreviewDataChange
    | TpaCompStateChange
    | TpaPostMessageToApp
    | LivePreviewSharedStateMessage
    | LivePreviewRoutersSharedStateMessage
    | WixCodeFileStateChange
    | PublishPipelineMessage
    | ViewerCacheReadyMessage
    | TriggerCreateRevision
    | ManifestUpdateMessage
    | ComponentOverrideIdUpdate
    | CustomComponentServerCloneDone
    | CustomComponentServerRestoreDone
    | TransactionsDroppedMessage
    | SitePropertiesInfoMessage
